import { compile } from 'path-to-regexp';

export enum PATHS {
  landing = '/',
  about = '/about',
  checkEmail = '/check-email',
  instructorRegistration = '/instructor-registration/:orgId',
  classes = '/classes',
  auth = '/auth',
  signIn = 'sign-in',
  signUp = 'sign-up',
  resetPassword = 'reset-password',
  emailValidation = 'email-validation',
  organizationRegistrationDone = '/org/registration-done',
  orgLanding = '/org/:orgId',
  enrollment = '/org/:orgId/enrollment',
  selectPublicSchool = '/org/:orgId/enrichment/:activityId',
  enrichmentRegistration = '/org/:orgId/enrichment/:activityId/registration',
  enrichmentDone = '/org/:orgId/enrichment/:activityId/done',
  orgActivityWithSessions = '/org/:orgId/activities/:activityType/:activityId',
  sessionRegistration = '/registration/session/:sessionId',
  instructorCertification = '/instructor-certification',
  instructorCertificationInOrganization = '/org/:orgId/instructor-certification',
  publicAttendance = '/org/:orgId/attendance/:hash',
  publicProductDescription = '/org/:orgId/products/:productId',
  productsLanding = '/products',
  jobPostingsList = '/org/:orgId/jobs',
  jobPostingDescription = '/org/:orgId/job/:jobId',
  jobPostingApplication = '/org/:orgId/job/:jobId/apply',
  registrationsForAdmin = '/admin/:orgId/registrations',
  organizationStudents = '/admin/:orgId/students',
  studentEnrollments = '/admin/:orgId/enrollments',
  studentEnrollment = '/admin/:orgId/enrollment/:enrollmentId',
  organizationCustomers = '/admin/:orgId/customers',
  sessions = '/admin/:orgId/sessions',
  sessionsCurrent = '/admin/:orgId/sessions/current',
  session = '/admin/:orgId/session/:id',
  addSession = '/admin/:orgId/session',
  sessionStudents = '/admin/:orgId/session/:sessionId/students',
  invoice = '/admin/:orgId/invoice/:invoiceId',
  paymentConfirmation = '/payment-confirmation',
  connectConfirmation = '/connect-confirmation',
  connectToPaymentPlatformConfirmation = '/connect-platform-confirmation',
  connectSchoolToEfacityConfirmation = '/connect-efacity-confirmation',
  invoiceAdditionalOptions = '/admin/:orgId/invoice/:invoiceId/additional-options',
  classroomsImport = '/sessions',
  studentSessions = '/student/:id/sessions',
  editActivity = '/admin/:orgId/activity/:id',
  addActivity = '/admin/:orgId/activity',
  activities = '/admin/:orgId/activities',
  editDiscount = '/admin/:orgId/discount/:id',
  addDiscount = '/admin/:orgId/discount',
  discounts = '/admin/:orgId/discounts',
  instructors = '/admin/:orgId/instructors',
  addInstructor = '/admin/:orgId/instructor',
  editInstructor = '/admin/:orgId/instructor/:id',
  organizationCredits = '/admin/:orgId/credits',
  centers = '/admin/:orgId/centers',
  addCenter = '/admin/:orgId/center',
  editCenter = '/admin/:orgId/centers/:id',
  organizationSettings = '/admin/:orgId/settings',
  additionalOptions = '/admin/:orgId/sessions/additionalOptions',
  additionalOptionsCurrent = '/admin/:orgId/sessions/additionalOptions/current',
  additionalOptionsExpired = '/admin/:orgId/sessions/additionalOptions/expired',
  addAdditionalOption = '/admin/:orgId/additionalOption',
  editAdditionalOption = '/admin/:orgId/additionalOption/:additionalOptionId',
  products = '/admin/:orgId/products',
  addProduct = '/admin/:orgId/product',
  editProduct = '/admin/:orgId/product/:productId',
  productPurchases = '/admin/:orgId/product-purchases',
  jobs = '/admin/:orgId/jobs',
  addJob = '/admin/:orgId/job',
  editJob = '/admin/:orgId/job/:jobId',
  messages = '/admin/:orgId/messages',
  tags = '/admin/:orgId/sessions/tags',
  tagsCurrent = '/admin/:orgId/sessions/tags/current',
  tagsExpired = '/admin/:orgId/sessions/tags/expired',
  addTag = '/admin/:orgId/tag',
  editTag = '/admin/:orgId/tag/:tagId',
  addLocation = '/admin/:orgId/settings/location',
  editLocation = '/admin/:orgId/settings/location/:locationId',
  locations = '/admin/:orgId/settings/locations',
  attendance = '/admin/:orgId/attendance',
  certificates = '/admin/:orgId/certificates',
  payments = '/admin/:orgId/payments',
  registrations = '/registrations',
  customerPurchases = '/purchases',
  customerCredits = '/credits',
  userSettings = '/settings',
  editStudent = '/students/:studentId',
  addStudent = '/student',
  students = '/students',
  selectStudentsForSessions = '/select-student',
  cart = '/cart',
  selectAdditionalOptionsByCustomer = '/select-additional-options',
  payment = '/payment/:invoiceId',
  notFound = '/not-found',
  userCertificates = '/certificates',
  reviewSession = '/review/session/:sessionId/:registrationId',
  courses = '/admin/:orgId/courses',
  usersEnrollmentApplications = '/enrollment-applications',
  customerStudentAgreements = '/customer/student/:studentId/agreements',
  // site admin routes
  siteAdmin = '/site-admin',
  siteAdminEditSchool = `${siteAdmin}/schools/:id`,
  siteAdminAddSchool = `${siteAdmin}/school`,
  siteAdminOrganizations = `${siteAdmin}/schools`,
  siteAdminPaymentPlatforms = `${siteAdmin}/payment-platforms/accounts`,
  siteAdminAddPaymentPlatform = `${siteAdmin}/payment-platform/account`,
  siteAdminEditPaymentPlatform = `${siteAdmin}/payment-platform/account/:id`,
  siteAdminLoginAudit = `${siteAdmin}/login-audit`,
  siteAdminReviews = `${siteAdmin}/reviews`,
  siteAdminUsers = `${siteAdmin}/users`,
  siteAdminAddUser = `${siteAdmin}/user`,
  siteAdminEditUser = `${siteAdmin}/user/:userId`
}

export const NEXT_JS_PATHS = [
  PATHS.about,
  '/org',
  '/registration/session',
  PATHS.productsLanding,
  PATHS.publicProductDescription,
  PATHS.instructorCertification,
  '/site-admin'
];

const isNextJsRoute = (path: string): boolean => {
  // In tests there is no window.location.pathname so path would be undefined
  if (!path) {
    return false;
  }

  if (path === PATHS.landing) {
    return true;
  }

  return !!NEXT_JS_PATHS.find((landingPath: string) => path.startsWith(landingPath));
};

export const shouldUseReactRouter = (toPath: string) => {
  const isCurrentLocationFromReact = !isNextJsRoute(window.location.pathname);
  const isToLocationFromReact = !isNextJsRoute(toPath);
  return isCurrentLocationFromReact === isToLocationFromReact;
};

/**
 * @param path such as '/user/:id.
 * @param params such as {id: 1}
 * @returns string such as '/user/1'
 */
export const toPath = (path: string, params: Record<string, unknown> = {}) => {
  // path-to-regexp v8 rejects non-string params outright, where v6 coerced them.
  // Callers legitimately pass numbers (ids, indexes), so keep coercing here.
  const stringParams: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    stringParams[key] = Array.isArray(value) ? value.map(String) : String(value);
  }
  return compile(path)(stringParams);
};

export const customerForStudentAgreementsPath = (studentId: string, agreementsIds: string[] = []) => {
  return agreementsIds?.length > 0
    ? `${toPath(PATHS.customerStudentAgreements, {
        studentId: studentId
      })}?agreement-id=${agreementsIds.join(',')}`
    : toPath(PATHS.customerStudentAgreements, { studentId: studentId });
};
